import {
  CreateAccountUserRoleRequest,
  CreateAccountUserRoleResponse,
  SubAccountsRequest,
  SubAccountsResponse,
} from 'vopay';
import type { RoleSubAccountCreateResult } from 'shared/users';

type Deps = {
  fetchSubAccountsAsync: (params: SubAccountsRequest) => Promise<SubAccountsResponse>;
  createUserRole: (params: CreateAccountUserRoleRequest, bearer?: string) => Promise<CreateAccountUserRoleResponse>;
};

/**
 * Fan-out helper: creates the same role in every active sub-account using the
 * sub-account's bearer token. Mirrors `propagateToSubAccounts` for workflows.
 *
 * Each sub-account is hit in parallel (Promise.all). Failures inside a single
 * sub-account don't abort the batch — we return a summary so the caller can
 * surface a partial-success message. A failure of the outer sub-accounts
 * fetch, however, throws so the caller doesn't mistake it for "zero
 * sub-accounts".
 */
export async function propagateRoleToSubAccounts(
  role: CreateAccountUserRoleRequest,
  { fetchSubAccountsAsync, createUserRole }: Deps,
): Promise<RoleSubAccountCreateResult> {
  const subAccountsRes = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });

  if (!subAccountsRes.Success) {
    throw new Error(subAccountsRes.ErrorMessage || 'Failed to fetch sub-accounts');
  }

  const subAccounts = subAccountsRes.Subaccounts;

  const results = await Promise.all(
    subAccounts.map(async (subAccount) => {
      try {
        await createUserRole(role, subAccount.Token);

        return { label: '', ok: true };
      } catch (error) {
        console.error(`Failed to create role "${role.Name}" in sub-account ${subAccount.AccountName}:`, error);

        return { label: subAccount.AccountName || subAccount.AccountID, ok: false };
      }
    }),
  );

  return {
    numTotal: subAccounts.length,
    numCreated: results.filter((r) => r.ok).length,
    failed: results.filter((r) => !r.ok).map((r) => r.label),
  };
}
